// Helper functions for getting and setting *unsecure* cookies on the client.
// Note that these should not be used for any sensitive information.

const deleteCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01-Jan-1970 00:00:01 GMT;`;
};

export const getCookie = (name: string): false | string => {
  const matchingCookie = decodeURIComponent(document.cookie)
    .split(';')
    .find((c) => c.includes(`${name}=`));
  // Safeguard against any irregularity in the name=val format (multiple '=' chars not supported)
  return matchingCookie != null && matchingCookie.split('=').length === 2 && matchingCookie.split('=')[1];
};

type SetCookieProps = { name: string; value: string; isProduction: boolean; durationInMinutes?: number };

export const setCookie = ({ name, value, isProduction, durationInMinutes }: SetCookieProps): void => {
  const domain = isProduction ? 'avalara.com' : 'avalara.io';
  const cookie = getCookie(name);
  if (cookie !== false) {
    deleteCookie(name);
  }

  // NOTE (Sasha): If no durationInMinutes, we want to set a session cookie
  const maxAge = durationInMinutes != null ? ` max-age=${durationInMinutes * 60};` : '';
  document.cookie = `${name}=${value}; domain=${domain};${maxAge} path=/; secure;`;
};

export const getAuiContextCookie = (): Record<string, unknown> => {
  const auiContextCookie = getCookie('aui-context');

  let parsedContextCookie: Record<string, unknown>;
  try {
    /* NOTE(Sasha): decode the auiContextCookie first since it may contain URL characters */
    parsedContextCookie = typeof auiContextCookie === 'string' ? JSON.parse(auiContextCookie) : {};
  } catch {
    parsedContextCookie = {};
  }
  return parsedContextCookie;
};

export const setAuiContextCookie = (key: string, value: string, isProduction: boolean): void => {
  const parsedContextCookie = getAuiContextCookie();

  const cookieValue = JSON.stringify({ ...parsedContextCookie, [key]: value });
  setCookie({ name: 'aui-context', value: cookieValue, isProduction });
};

type ActiveAccountIdProps = {
  accountId: string;
  isTechSupport: boolean;
  aaUrl: string | null;
  returnUrl?: string;
};

export const getActiveAccountId = ({ accountId, isTechSupport, aaUrl, returnUrl }: ActiveAccountIdProps) => {
  if (!isTechSupport) return accountId;

  const auiContextCookie = getAuiContextCookie();
  const { activeAccountId } = auiContextCookie;
  if (!activeAccountId) {
    const returnString = returnUrl != null ? `?returnUrl=${returnUrl}` : '';
    window.location.assign(
      `${aaUrl != null ? aaUrl : 'https://account.avalara.com'}/#/goTo/select-account${returnString}`,
    );
    return null;
  }
  return activeAccountId;
};
